import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import ReadOnlyInput from '../../../../components/ReadOnlyInput';
import Dropdown from '../../../../components/Dropdown';
import Table from '../../../../components/Table';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import styles from '../css/ChronicMedicationQuickView.module.css'
import overlayStyles from '../../../../css/components/overlaymodal-animations.module.css';
import { CompletedPopup } from '../../ToFollows/views/FollowUpSignatureFlow.jsx';
import { fetchScriptDetails } from '../../../../api/dispensary/script/processScriptApi';
import { useAuth } from '../../../../context/AuthContext.jsx';
import { markReturned } from '../../../../api/dispensary/script/returnsApi';

const ChronicMedicationQuickView = () => {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const parsedPatientId = parseInt(patientId, 10);
    const location = useLocation();
    const paymentTypeFromState = location.state?.paymentType || 'Cash';
    const mode = location.state?.mode || 'default'; // 'returnInventory' to enable return flow
    const repeatId = location.state?.repeatId || null;

    const { user } = useAuth();

    const [patientData, setPatientData] = useState(null);
    const [patientName, setPatientName] = useState('');
    const [practitioner, setPractitioner] = useState('');
    const [pharmacist, setPharmacist] = useState('');
    const [scriptMeta, setScriptMeta] = useState(null);
    const [medications, setMedications] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);
    const [totalQuantity, setTotalQuantity] = useState(0);

    useEffect(() => {
        const load = async () => {
            try {
                // parsedPatientId is actually the prescriptionId from the cards
                const data = await fetchScriptDetails(parsedPatientId);
                if (!data) return;

                const script = data.script || {};
                setScriptMeta({
                    type: script.type || '',
                    processscriptnumber: script.processscriptnumber || '',
                    clinicreference: script.clinicreference || '',
                    paymentType: script.paymentType || paymentTypeFromState,
                    scriptType: (script.scriptType || '').toUpperCase() || 'PR',
                    // Carry through any existing script document URL so the
                    // chronic-script-view "View" button can open it.
                    docurl: script.docurl || null,
                });

                const patient = data.patient || {};

                const patientLabel = `${patient.surname || ''} ${patient.firstname || ''}`.trim();
                setPatientName(patientLabel);

                const profileFromDetails =
                    patient.profileNo ||
                    patient.profileno ||
                    patient.profileNumber ||
                    patient.profilenumber ||
                    null;

                const addrFromDetails = [];
                if (patient.homeStreet) addrFromDetails.push(patient.homeStreet);
                if (patient.homeSuburb) addrFromDetails.push(patient.homeSuburb);
                if (patient.homeCity) addrFromDetails.push(patient.homeCity);
                if (patient.homePostalCode) addrFromDetails.push(patient.homePostalCode);
                if (!addrFromDetails.length && patient.homeaddress) {
                    addrFromDetails.push(String(patient.homeaddress));
                }
                // Some APIs return a single combined address field
                if (!addrFromDetails.length && patient.address) {
                    addrFromDetails.push(String(patient.address));
                }

                const phoneFromDetails =
                    patient.contactNumber ||
                    patient.contactnumber ||
                    patient.cell ||
                    patient.cellphone ||
                    patient.cellphoneNo ||
                    patient.mobile ||
                    patient.phone ||
                    patient.telephone ||
                    '';

                const medicalAidNumberFromDetails = data.medicalAid?.number || null;

                const patientIdFromDetails =
                    patient.id ||
                    patient.patientId ||
                    patient.patientid ||
                    patient.patientID ||
                    data.patientId ||
                    (data.script && (data.script.patientId || data.script.patientid)) ||
                    null;

                const nextPatientData = {
                    id: parsedPatientId,
                    patientId: patientIdFromDetails,
                    profileNo: profileFromDetails,
                    addressLines: addrFromDetails,
                    phone: phoneFromDetails,
                    medicalAidNumber: medicalAidNumberFromDetails,
                };

                console.log('[ChronicMedicationQuickView] fetched script details', {
                    parsedPatientId,
                    data,
                    patient,
                    nextPatientData,
                });

                setPatientData(nextPatientData);

                const pracLabel = data.practitioner
                    ? `${data.practitioner.title || ''} ${data.practitioner.firstname || ''} ${data.practitioner.surname || ''}`.trim()
                    : '';
                setPractitioner(pracLabel);
                const meds = (data.drugs || []).map(d => ({
                    prescriptionDrugId: d.prescriptionDrugId,
                    sku: d.sku,
                    drugName: d.drugName,
                    packSize: d.packSize,
                    icd10: d.icd10,
                    strength: d.strength,
                    qty: d.quantity,
                    nappiCode: d.nappiCode,
                    instructions: d.instructions,
                    costPrice: typeof d.costPrice === 'number' || (typeof d.costPrice === 'string' && !d.costPrice.startsWith('R')) ? `R${d.costPrice}` : (d.costPrice || ''),
                    checked: false,
                }));
                setMedications(meds);
                const totalQ = meds.reduce((sum, m) => sum + (parseInt(m.qty, 10) || 0), 0);
                setTotalQuantity(totalQ);
            } catch (e) {
                console.error('Failed to load script details', e);
            }
        };
        if (Number.isFinite(parsedPatientId)) load();
    }, [parsedPatientId]);

    // Prefill pharmacist from logged-in user (Firstname/Name + Surname)
    useEffect(() => {
        if (!user) return;
        const first = (user.firstname || user.name || '').toString().trim();
        const last = (user.surname || '').toString().trim();
        const full = [first, last].filter(Boolean).join(' ');
        if (full) setPharmacist(full);
    }, [user]);

    const columns = [
        { header: 'Drug Name', accessor: 'drugName' },
        { header: 'Pack Size', accessor: 'packSize' },
        { header: 'ICD-10', accessor: 'icd10' },
        { header: 'Strength', accessor: 'strength' },
        { header: 'Qty', accessor: 'qty' },
        { header: 'Nappi Code', accessor: 'nappiCode' },
        { header: 'Instructions', accessor: 'instructions' },
        { header: 'Cost Price', accessor: 'costPrice' },
        ...(mode === 'returnInventory' ? [] : [{
            header: 'Check',
            accessor: 'checked',
            render: (value, row, rowIndex) => (
                <Checkbox
                    id={`check-${rowIndex}`}
                    checked={value}
                    onChange={(e) => {
                        const newMedications = [...medications];
                        newMedications[rowIndex].checked = e.target.checked;
                        setMedications(newMedications);
                    }}
                    disabled={false}
                />
            ),
        }]),
    ];

    const handleProcess = () => {
        const selectedMedications = medications.filter(med => med.checked);
        if (mode === 'returnInventory') {
            // In return mode, show confirmation instead of processing script
            setShowConfirm(true);
            return;
        }
        if (selectedMedications.length > 0 && scriptMeta) {
            const { type, processscriptnumber, clinicreference, paymentType, scriptType, scriptDocUrl, docurl } = scriptMeta;

            const total = selectedMedications.reduce((sum, med) => {
                const raw = typeof med.costPrice === 'string' ? med.costPrice : String(med.costPrice ?? '0');
                const cleaned = raw.replace(/[^0-9.\-]/g, '');
                const n = parseFloat(cleaned);
                return sum + (Number.isFinite(n) ? n : 0);
            }, 0);

            console.log('[ChronicMedicationQuickView] navigating to chronic-script-view', {
                prescriptionId: parsedPatientId,
                scriptMeta,
                patientData,
                selectedMedications,
                total,
            });

            navigate('/customer/dispensary/chronic-script-view', {
                state: {
                    type: type || 'process',
                    repeatId,
                    prescriptionId: parsedPatientId,
                    scriptData: {
                        type: type || 'process',
                        processscriptnumber: processscriptnumber || '',
                        clinicreference: clinicreference || '',
                        scriptNumber: processscriptnumber || clinicreference || '',
                        patient: patientName,
                        practitioner: practitioner,
                        pharmacist: pharmacist,
                        drugs: selectedMedications,
                        total,
                        paymentType: paymentType || paymentTypeFromState,
                        scriptType: scriptType || 'PR',
                        scriptDocUrl: scriptDocUrl || docurl || null,

                        patientId: patientData.patientId || null,
                        profileNo: patientData.profileNo || null,
                        addressLines: patientData.addressLines || [],
                        phone: patientData.phone || '',
                        medicalAidNumber: patientData.medicalAidNumber || null,
                    },
                },
            });
        } else {
            console.log('No medications selected');
        }
    };

    const handleConfirmReturn = async () => {
        try {
            await markReturned({
                prescriptionid: parsedPatientId,
                ...(repeatId ? { repeatid: repeatId } : {}),
            });
            setShowConfirm(false);
            setShowCompleted(true);
        } catch (e) {
            console.error('Failed to mark returned', e);
            setShowConfirm(false);
        }
    };

    const confirmDialog = (
        showConfirm && (
            <div className={`${overlayStyles.modalOverlay} ${overlayStyles.show}`}>
                <div
                    className={`${styles.modalContent} ${overlayStyles.stepEnter}`}
                >
                    <button className={overlayStyles.closeButton} onClick={() => setShowConfirm(false)} aria-label="Close">×</button>
                    <div className={styles.modalHeader}>
                        <div className={styles.warningIcon}>
                            !
                        </div>
                        <h3 className={styles.modalTitle}>Are you sure you want to return these items to inventory?</h3>
                        <div className={styles.modalActions}>
                            <Button variant="primary" onClick={handleConfirmReturn}>Return</Button>
                            <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    );

    if (!patientData) return <div>Loading...</div>;

    return (
        <div className={styles.container}>
            <Navbar backTo="/customer/dispensary/chronic-medication" />
            <main className={styles.main}>
                <h1 className={styles.title}>Patient Medication Details</h1>
                <div className={styles.formContainer}>
                    <ReadOnlyInput
                        label="Patient Name"
                        value={patientName}
                    />
                    <ReadOnlyInput
                        label="Practitioner"
                        value={practitioner}
                    />
                    <ReadOnlyInput
                        label="Total Quantity"
                        value={String(totalQuantity)}
                    />
                    <ReadOnlyInput
                        label="Pharmacist"
                        value={pharmacist}
                    />
                </div>
                <div className={styles.tableContainer}>
                    <Table columns={columns} data={medications} />
                </div>
                <div className={styles.buttonContainer}>
                    <Button variant="primary" onClick={handleProcess}>
                        {mode === 'returnInventory' ? 'Return To Inventory' : 'Process'}
                    </Button>
                </div>
            </main>
            <Footer />
            {confirmDialog}
            <CompletedPopup isOpen={showCompleted} onClose={() => { setShowCompleted(false); navigate('/customer/dispensary/chronic-medication'); }} />
        </div>
    );
};

export default ChronicMedicationQuickView;